import { createContext, useContext, useEffect } from "react";
import { action, computed, makeAutoObservable, observable } from "mobx";
import { useLocalObservable, observer } from "mobx-react-lite";

import { Cookie, Header } from "./types";
import { ParameterValidationErrors, validateParameter } from "./ParameterBuilderDialog/validation";

import { ParameterStore } from "@/components/ModelParameters/store/parameter-store";
import { Model, Parameter, ComponentType } from "@/queries/models/types";
import { HUGGINGFACE_PROVIDER } from "@/components/ModelParameters/constants";
import { useModels } from "@/queries/models";
export class CustomParametersStore {
  @observable private _parameters = new Map<string, ParameterStore>();
  @observable private _currentParameter: ParameterStore | null = null;
  @observable private _previousState: ParameterStore | null = null;
  @observable private _cookies: Cookie[] = [];
  @observable private _headers: Header[] = [];
  @observable private _timeout = 1;
  @observable private _isViewing = false;
  @observable private _isEditing = false;
  @observable private _copyOfExistingParameters: Map<string, ParameterStore> | null = null;
  @observable private _models: Model[] = [];
  @observable private _providerId: number | undefined = undefined;
  @observable private _validationErrors: ParameterValidationErrors = {};
  constructor() {
    makeAutoObservable(this);
    this._parameters = new Map();
    this._currentParameter = null;
    this._previousState = null;
    this._copyOfExistingParameters = null;
  }

  @action
  setModels(models: Model[]) {
    this._models = models;
  }

  @action
  removeParameter(paramId: string) {
    this._parameters.delete(paramId);
  }

  @action
  restorePreviousState() {
    if (!this._previousState) {
      return;
    }

    const paramId = this._previousState?.paramId ?? "";

    if (this._parameters.has(paramId)) {
      this._parameters.set(paramId, this._previousState);
      this._currentParameter = this._previousState;
    }
  }

  @action
  setIsViewing(isViewing: boolean) {
    this._isViewing = isViewing;
  }

  @action
  setIsEditing(isEditing: boolean) {
    this._isEditing = isEditing;
    this.captureSavedParametersSnapshot();
  }

  @action
  captureSavedParametersSnapshot() {
    this._copyOfExistingParameters = new Map(
      Array.from(this._parameters.values()).map(parameter => [
        parameter.paramId ?? "",
        new ParameterStore(parameter.getParameter),
      ]),
    );
  }

  @action
  restoreSavedParameters() {
    this._parameters = new Map(
      Array.from((this._copyOfExistingParameters ?? new Map()).entries()).map(
        ([paramId, parameter]) => [paramId, new ParameterStore(parameter.getParameter)],
      ),
    );
    this.clearParameterSelection();
  }

  @action
  addCookie(cookie: Cookie) {
    this._cookies.push(cookie);
  }

  @action
  setCookies(cookies: Cookie[]) {
    this._cookies = cookies;
  }

  @action
  setHeaders(headers: Header[]) {
    this._headers = headers;
  }

  @action
  generateParameterId(type: ComponentType) {
    let highestNumber = 0;

    this.parametersList.forEach(param => {
      if (param.paramId && param.paramId.toLowerCase().startsWith(`${type}_`)) {
        const number = Number(param.paramId.split("_")[1]);
        if (!isNaN(number) && number > highestNumber) {
          highestNumber = number;
        }
      }
    });

    return `${type}_${highestNumber + 1}`;
  }

  @action
  updateCookie(cookie: Cookie) {
    const index = this._cookies.findIndex(c => c.id === cookie.id);
    if (index !== -1) {
      this._cookies[index] = cookie;
    }
  }

  @action
  removeCookie(id: number) {
    this._cookies = this._cookies.filter(cookie => cookie.id !== id);
  }

  @action
  addHeader(header: Header) {
    this._headers.push(header);
  }

  @action
  updateHeader(header: Header) {
    const index = this._headers.findIndex(h => h.id === header.id);
    if (index !== -1) {
      this._headers[index] = header;
    }
  }

  @action
  removeHeader(id: number) {
    this._headers = this._headers.filter(header => header.id !== id);
  }

  @action
  setTimeout(timeout: number) {
    this._timeout = timeout;
  }

  @action
  saveParameter(parameter: ParameterStore) {
    const paramData = { ...parameter.getParameter };

    if ("is_draft" in paramData) {
      delete paramData.is_draft;
    }

    const previousParamId = this._previousState?.paramId;
    const nextParamId = parameter.paramId ?? "";

    if (previousParamId && previousParamId !== nextParamId) {
      this._parameters.delete(previousParamId);
    }

    this._parameters.set(nextParamId, new ParameterStore(paramData));
    this.clearValidationErrors();
  }

  @action
  validateCurrentParameter(): boolean {
    if (!this._currentParameter) {
      return false;
    }

    if (this._currentParameter.name) {
      this._currentParameter.updateName(this._currentParameter.name.trim());
    }

    if (this._currentParameter.paramId) {
      this._currentParameter.updateParamId(this._currentParameter.paramId.trim());
    }

    const existingParamIds = this.parametersList
      .filter(parameter => parameter !== this._currentParameter)
      .map(parameter => parameter.paramId?.trim() ?? "")
      .filter(Boolean);

    const result = validateParameter(this._currentParameter, existingParamIds);
    this._validationErrors = result.errors;

    return result.isValid;
  }

  @action
  clearValidationError(field: keyof ParameterValidationErrors) {
    if (!this._validationErrors[field]) {
      return;
    }

    const nextErrors = { ...this._validationErrors };
    delete nextErrors[field];
    this._validationErrors = nextErrors;
  }

  @action
  clearValidationErrors() {
    this._validationErrors = {};
  }

  @action
  setCurrentParameter(parameter: ParameterStore | null) {
    this._currentParameter = parameter;
    this.clearValidationErrors();
  }

  @action
  clearParameterSelection() {
    this._currentParameter = null;
    this._previousState = null;
    this.clearValidationErrors();
  }

  @action
  inheritableParameters(model: Model) {
    return model.params?.filter(parameter => !this._parameters.has(parameter.param_id));
  }

  @action
  inheritParameters(model: Model) {
    const inheritableParameters = this.inheritableParameters(model);

    if (!inheritableParameters) {
      return;
    }

    inheritableParameters.forEach(parameter => {
      this.saveParameter(new ParameterStore(parameter));
    });
  }

  @action
  removeAllParameters() {
    this._parameters = new Map();
    this._currentParameter = null;
    this._previousState = null;
  }

  @action
  reset() {
    this._parameters = new Map();
    this._currentParameter = null;
    this._previousState = null;
    this._cookies = [];
    this._headers = [];
    this._timeout = 1;
    // Keep the dirty-check baseline in sync so close/save paths don't
    // treat a cleared store as having unsaved parameter changes.
    this._copyOfExistingParameters = new Map();
  }

  @action
  setProviderId(providerId: number) {
    this._providerId = providerId;
  }

  @computed get providerId() {
    return this._providerId;
  }

  @action
  saveCurrentParameterState(parameter?: ParameterStore) {
    if (!parameter) {
      this._previousState = this.currentParameter;

      return;
    }

    this._previousState = new ParameterStore({ ...parameter.getParameter });
  }

  @action
  setSavedParameters(parameters: Parameter[]) {
    this._parameters = new Map(
      parameters.map(parameter => [parameter.param_id, new ParameterStore(parameter)]),
    );
    this.captureSavedParametersSnapshot();
  }

  @action
  getKnownModel(model: string, provider: string, isChat: boolean) {
    if (provider === HUGGINGFACE_PROVIDER) {
      return this._models.find(p => p.is_chat === isChat);
    }

    return this._models.filter(m => !m.display_name).find(m => m.llm_model_name === model);
  }

  @computed get parametersList() {
    return Array.from(this._parameters.values());
  }

  @computed get modelsList() {
    return this._models;
  }

  @computed get cookies() {
    return this._cookies;
  }

  @computed get headers() {
    return this._headers;
  }

  @computed get timeout() {
    return this._timeout;
  }

  @computed get isEditingParameter() {
    return !!this.parametersList.find(
      parameter => parameter.paramId === this.currentParameter?.paramId,
    );
  }

  @computed get currentParameter() {
    return this._currentParameter;
  }

  @computed get hasParameters() {
    return this.parametersList.length > 0;
  }

  @computed get areParametersDifferent() {
    const serialize = (parameters: Map<string, ParameterStore> | null) =>
      JSON.stringify(
        Array.from((parameters ?? new Map()).values(), parameter => parameter.getParameter),
      );

    return serialize(this._copyOfExistingParameters) !== serialize(this._parameters);
  }

  @computed get isViewing() {
    return this._isViewing;
  }

  @computed get isEditing() {
    return this._isEditing;
  }

  @computed get hasPreviousState() {
    return !!this._previousState;
  }

  @computed get isStateDirty() {
    return JSON.stringify(this._previousState) !== JSON.stringify(this._currentParameter);
  }

  @computed get validationErrors() {
    return this._validationErrors;
  }
}

const CustomParametersStoreContext = createContext<CustomParametersStore | null>(null);

export const CustomParametersStoreProvider = observer(
  ({ children }: { children: React.ReactNode }) => {
    const store = useLocalObservable(() => new CustomParametersStore());
    const { data: models } = useModels({
      providerId: store.providerId,
    });

    useEffect(() => {
      if (models) {
        store.setModels(models.model_configs);
      }
    }, [models, store]);

    return (
      <CustomParametersStoreContext.Provider value={store}>
        {children}
      </CustomParametersStoreContext.Provider>
    );
  },
);

export const useCustomParametersStore = () => {
  const store = useContext(CustomParametersStoreContext);
  if (!store) {
    throw new Error("useCustomParametersStore must be used within a CustomParametersStoreProvider");
  }
  return store;
};
